import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, X } from 'react-feather';

/**
 * MultiDatePicker — unified calendar that supports both modes in one widget:
 *   • Click a day to toggle it on/off (sparse multi-select)
 *   • Shift+click a day to fill the range from the most-recently-clicked anchor
 *     to that day (inclusive). All days in between get added.
 *
 * Calling code owns the selected-dates array (ISO yyyy-mm-dd strings, sorted
 * ascending). Future dates are blocked. Max selection is enforced by the caller.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const isoOf = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const fromISO = (iso) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d);
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MultiDatePicker = ({ selected = [], onChange, max = 31, maxDate }) => {
    const { t } = useTranslation();
    const today = useMemo(() => {
        const t = new Date();
        t.setHours(0, 0, 0, 0);
        return t;
    }, []);
    const ceiling = maxDate || today;

    const [cursor, setCursor] = useState(() => startOfMonth(today));
    const [anchor, setAnchor] = useState(null); // ISO of last single-click

    const selectedSet = useMemo(() => new Set(selected), [selected]);

    // Build day cells for the current month grid. Pad the leading edge with
    // trailing days from the previous month so the grid always starts on Monday.
    const cells = useMemo(() => {
        const firstOfMonth = startOfMonth(cursor);
        // 0=Sun..6=Sat in JS. Convert to Mon=0..Sun=6.
        const leadingBlank = (firstOfMonth.getDay() + 6) % 7;
        const start = new Date(firstOfMonth);
        start.setDate(firstOfMonth.getDate() - leadingBlank);
        const out = [];
        for (let i = 0; i < 42; i += 1) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            out.push(d);
        }
        return out;
    }, [cursor]);

    const inCurrentMonth = (d) => d.getMonth() === cursor.getMonth();
    const isFuture = (d) => d.getTime() > ceiling.getTime();

    const toggleSingle = useCallback(
        (iso) => {
            const next = new Set(selectedSet);
            if (next.has(iso)) {
                next.delete(iso);
            } else {
                if (next.size >= max) return;
                next.add(iso);
            }
            const arr = Array.from(next).sort();
            onChange(arr);
            setAnchor(iso);
        },
        [selectedSet, onChange, max],
    );

    const fillRange = useCallback(
        (toIso) => {
            if (!anchor) {
                toggleSingle(toIso);
                return;
            }
            const a = fromISO(anchor);
            const b = fromISO(toIso);
            const lo = a.getTime() <= b.getTime() ? a : b;
            const hi = a.getTime() <= b.getTime() ? b : a;
            const days = Math.round((hi.getTime() - lo.getTime()) / MS_PER_DAY) + 1;
            const next = new Set(selectedSet);
            for (let i = 0; i < days; i += 1) {
                const d = new Date(lo);
                d.setDate(lo.getDate() + i);
                if (isFuture(d)) break;
                const iso = isoOf(d);
                if (!next.has(iso)) {
                    if (next.size >= max) break;
                    next.add(iso);
                }
            }
            onChange(Array.from(next).sort());
            setAnchor(toIso);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [anchor, selectedSet, onChange, max, ceiling],
    );

    const handleDayClick = (d, event) => {
        if (isFuture(d)) return;
        const iso = isoOf(d);
        if (event.shiftKey) fillRange(iso);
        else toggleSingle(iso);
    };

    const removeDate = (iso) => {
        const next = new Set(selectedSet);
        next.delete(iso);
        onChange(Array.from(next).sort());
    };

    const clearAll = () => {
        onChange([]);
        setAnchor(null);
    };

    const prevMonth = () => {
        const c = new Date(cursor);
        c.setMonth(c.getMonth() - 1);
        setCursor(c);
    };
    const nextMonth = () => {
        const c = new Date(cursor);
        c.setMonth(c.getMonth() + 1);
        // Don't allow the cursor to land in a month entirely past the ceiling
        if (startOfMonth(c).getTime() > startOfMonth(ceiling).getTime()) return;
        setCursor(c);
    };

    return (
        <div className="v3-mdp">
            <div className="v3-mdp-head">
                <button
                    type="button"
                    className="v3-mdp-nav"
                    onClick={prevMonth}
                    aria-label={t('Previous month')}
                >
                    <ChevronLeft size={16} />
                </button>
                <div className="v3-mdp-jump">
                    <select
                        className="v3-mdp-jump-select"
                        value={cursor.getMonth()}
                        onChange={(e) => {
                            const m = Number(e.target.value);
                            const c = new Date(cursor.getFullYear(), m, 1);
                            // Don't allow landing in a month entirely past the ceiling.
                            if (c.getTime() > startOfMonth(ceiling).getTime()) {
                                setCursor(startOfMonth(ceiling));
                            } else {
                                setCursor(c);
                            }
                        }}
                        aria-label={t('Month')}
                    >
                        {MONTH_NAMES.map((m, i) => {
                            const disabled = new Date(cursor.getFullYear(), i, 1).getTime() > startOfMonth(ceiling).getTime();
                            return (
                                <option key={m} value={i} disabled={disabled}>{t(m)}</option>
                            );
                        })}
                    </select>
                    <select
                        className="v3-mdp-jump-select"
                        value={cursor.getFullYear()}
                        onChange={(e) => {
                            const y = Number(e.target.value);
                            const c = new Date(y, cursor.getMonth(), 1);
                            if (c.getTime() > startOfMonth(ceiling).getTime()) {
                                setCursor(startOfMonth(new Date(y, ceiling.getMonth(), 1)));
                            } else {
                                setCursor(c);
                            }
                        }}
                        aria-label={t('Year')}
                    >
                        {Array.from({ length: 11 }, (_, i) => ceiling.getFullYear() - 10 + i).map((y) => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>
                <button
                    type="button"
                    className="v3-mdp-nav"
                    onClick={nextMonth}
                    aria-label={t('Next month')}
                    disabled={
                        startOfMonth(cursor).getTime() >= startOfMonth(ceiling).getTime()
                    }
                >
                    <ChevronRight size={16} />
                </button>
            </div>

            <div className="v3-mdp-weekdays">
                {WEEKDAYS.map((w) => (
                    <span key={w}>{t(w)}</span>
                ))}
            </div>

            <div className="v3-mdp-grid" role="grid">
                {cells.map((d) => {
                    const iso = isoOf(d);
                    const out = !inCurrentMonth(d);
                    const sel = selectedSet.has(iso);
                    const isToday = sameDay(d, today);
                    const future = isFuture(d);
                    return (
                        <button
                            key={iso}
                            type="button"
                            className={[
                                'v3-mdp-cell',
                                out ? 'out' : '',
                                sel ? 'sel' : '',
                                isToday ? 'today' : '',
                                future ? 'disabled' : '',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                            onClick={(e) => handleDayClick(d, e)}
                            disabled={future}
                            aria-pressed={sel}
                            aria-label={iso}
                        >
                            {d.getDate()}
                        </button>
                    );
                })}
            </div>

            <div className="v3-mdp-hint">
                {t('Click toggles a day.')} <strong>{t('Shift+click')}</strong> {t('fills the range from the last clicked day.')}
            </div>

            <div className="v3-mdp-summary">
                <span className="v3-mdp-count">
                    <strong>{selected.length}</strong> / {max} {t('dates')}
                </span>
                {selected.length > 0 && (
                    <button type="button" className="v3-mdp-clear" onClick={clearAll}>
                        {t('Clear all')}
                    </button>
                )}
            </div>

            {selected.length > 0 && (
                <div className="v3-mdp-chips">
                    {selected.map((iso) => (
                        <span key={iso} className="v3-mdp-chip">
                            {iso}
                            <button
                                type="button"
                                className="v3-mdp-chip-x"
                                onClick={() => removeDate(iso)}
                                aria-label={t('Remove {{date}}', { date: iso })}
                            >
                                <X size={11} />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MultiDatePicker;
